import { FunctionComponent, useEffect, useState } from 'react'
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper'
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import FileUploadAllLineService from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'

export const DeleteImportFileDialog: FunctionComponent<{
  open: boolean,
  uploadId: string,
  fileType: string,
  isReportPeriodLocked: boolean,
  closeHandler: () => void,
  title: string,
  cancelHandler: () => void,
  submitHandler: (deleteAssociatedRecords: boolean) => void,
  isSubmitting: boolean,
}> = ({
  open,
  uploadId,
  fileType,
  isReportPeriodLocked,
  closeHandler,
  title,
  cancelHandler,
  submitHandler,
  isSubmitting,
}) => {
    const [hasCommittedLines, setHasCommittedLines] = useState<boolean | null>(null)
    const [deleteFileOnly, setDeleteFileOnly] = useState(false)
    const [deleteFileAndAssociatedRecords, setDeleteFileAndAssociatedRecords] = useState(false)
    const [confirmationText, setConfirmationText] = useState('')
    const [checkError, setCheckError] = useState<string>()

    const isEmissionsType = fileType.startsWith('emissions')
    const isSpendBasedType = fileType === 'spend-based'
    const isActionNotAllowed = isReportPeriodLocked && (isEmissionsType || isSpendBasedType)

    useEffect(() => {
      if (open && !isActionNotAllowed && uploadId && fileType) {
        const service = new FileUploadAllLineService()
        setHasCommittedLines(null)
        setCheckError(undefined)
        service.getUploadLinesWithPaginationByFile(fileType, uploadId, 'committed', 0, 1)
          .then(response => {
            setHasCommittedLines(response.count > 0)
          })
          .catch(err => {
            console.log(err)
            setHasCommittedLines(false)
            setCheckError('There was an error checking whether there are committed records. Please try again.')
          })
      }
      if (!open) {
        setDeleteFileOnly(false)
        setDeleteFileAndAssociatedRecords(false)
        setConfirmationText('')
        setHasCommittedLines(null)
        setCheckError(undefined)
      }
    }, [open, isActionNotAllowed, uploadId, fileType])

    useEffect(() => {
      if (
        open
        && hasCommittedLines
        && !isActionNotAllowed
        && !deleteFileOnly
        && !deleteFileAndAssociatedRecords
      ) {
        setDeleteFileOnly(true)
      }
    }, [open, hasCommittedLines, isActionNotAllowed, deleteFileOnly, deleteFileAndAssociatedRecords])

    const recordLabel = () => {
      if (fileType === 'locations') return 'locations'
      if (fileType === 'vehicles') return 'vehicles'
      if (fileType === 'products') return 'products'
      if (fileType === 'projects') return 'projects'
      if (fileType === 'suppliers') return 'suppliers'
      if (isSpendBasedType || isEmissionsType) return 'emissions'
      return fileType
    }

    const handleSubmit = () => {
      if (isActionNotAllowed) {
        return
      }
      let deleteAssociatedRecords = false
      if (hasCommittedLines) {
        if (!deleteFileOnly && !deleteFileAndAssociatedRecords) {
          return
        }
        if (deleteFileAndAssociatedRecords) {
          if (confirmationText.trim().toLowerCase() !== 'delete') {
            return
          }
          deleteAssociatedRecords = true
        }
      }
      submitHandler(deleteAssociatedRecords)
    }

    const disableSaveButton = isActionNotAllowed
      || (hasCommittedLines === true && !deleteFileOnly && !deleteFileAndAssociatedRecords)
      || (hasCommittedLines === true && deleteFileAndAssociatedRecords && confirmationText.trim().toLowerCase() !== 'delete')

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title={title}
        cancelHandler={cancelHandler}
        submitHandler={handleSubmit}
        isSubmitting={isSubmitting}
        disableSaveButton={disableSaveButton}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isActionNotAllowed && (
            <Typography variant='body1'>
              The selected report period is locked and cannot be modified.
            </Typography>
          )}
          {!isActionNotAllowed && hasCommittedLines === false && (
            <Typography variant='body1'>
              Are you sure you want to delete this file? This action cannot be undone.
            </Typography>
          )}
          {!isActionNotAllowed && hasCommittedLines === null && !checkError && (
            <Typography variant='body1'>
              Checking whether there are any committed records for this file&hellip;
            </Typography>
          )}
          {!isActionNotAllowed && hasCommittedLines && (
            <>
              <Typography variant='body1'>
                This file has committed {recordLabel()} records. Please confirm whether you want to delete the file only, or the file and all associated {recordLabel()} records.
              </Typography>
              <FormControlLabel
                sx={{ ml: 0.5 }}
                control={
                  <Checkbox
                    checked={deleteFileOnly}
                    onChange={e => {
                      const checked = e.target.checked
                      setDeleteFileOnly(checked)
                      if (checked) {
                        setDeleteFileAndAssociatedRecords(false)
                        setConfirmationText('')
                      }
                    }}
                    color='primary'
                  />
                }
                label={`Delete this file only. Existing ${recordLabel()} records will be retained.`}
              />
              <FormControlLabel
                sx={{ ml: 0.5 }}
                control={
                  <Checkbox
                    checked={deleteFileAndAssociatedRecords}
                    onChange={e => {
                      const checked = e.target.checked
                      setDeleteFileAndAssociatedRecords(checked)
                      if (checked) {
                        setDeleteFileOnly(false)
                      }
                      if (!checked) {
                        setConfirmationText('')
                      }
                    }}
                    color='primary'
                  />
                }
                label={`Delete this file and all associated ${recordLabel()} records.`}
              />
              {deleteFileAndAssociatedRecords && (
                <TextField
                  label="Type 'delete' to confirm"
                  value={confirmationText}
                  onChange={e => setConfirmationText(e.target.value)}
                  fullWidth
                />
              )}
            </>
          )}
          {checkError && !isActionNotAllowed && (
            <Typography variant='body2' color='error'>
              {checkError}
            </Typography>
          )}
        </Box>
      </DialogWrapper>
    )
  }